import React, { useEffect } from "react"
import PropTypes from "prop-types"
import { Button, Card, CardBody, Container } from "reactstrap"

import userStore from "userStore"
import { useHistory } from "react-router-dom"

import useAuthorization from "hooks/useAuthorization"
import Loading from "components/Loading"

/**
 * Oculta o bloquea contenido según el rol del usuario. BDSJEA-35
 *
 * Dos modos, según `pantalla`:
 *
 * - `false` (default): si no tiene el rol, no renderiza nada. Es lo que se usa
 *   para botones y acciones sueltas, donde mostrar algo deshabilitado solo
 *   genera la pregunta de por qué no anda.
 * - `true`: muestra un mensaje de acceso denegado con un botón para volver. Se
 *   usa envolviendo una pantalla entera, para el caso en que alguien fuerza la
 *   URL a mano.
 *
 * Esto es cosmético: la autorización real la aplica el backend con
 * `@PreAuthorize`. Si los dos se desincronizan, gana el 403.
 */
const RequireRole = ({
  roles,
  objeto,
  accion,
  pantalla,
  children,
  fallback,
}) => {
  const { tieneAlguno, puedeConsultarObjeto, puedeModificarObjeto } =
    useAuthorization()
  const loading = userStore(state => state.loading)
  const history = useHistory()

  // Dos modos. Con "objeto" resuelve contra la matriz de Permisos por Perfil,
  // que es lo que corresponde para casi todo: si el administrador habilita algo
  // en pantalla, tiene que verse. Con "roles" queda fijo, y eso solo aplica a
  // los objetos no delegables que el backend tambien tiene clavados.
  const permitido = objeto
    ? accion === "modificacion"
      ? puedeModificarObjeto(objeto)
      : puedeConsultarObjeto(objeto)
    : tieneAlguno(...(roles || []))

  // Antes lo escribía Permission, cuando encontraba el objeto. Ahora lo hace
  // el mismo componente que resolvió el permiso, así el valor siempre
  // corresponde a la pantalla en la que estás y no queda pegado el de la
  // anterior. Las pantallas lo leen para habilitar el botón de Guardar.
  useEffect(() => {
    if (!pantalla) return
    userStore.setState({
      modificacion: objeto ? puedeModificarObjeto(objeto) : permitido,
    })
  }, [objeto, permitido, pantalla])

  if (permitido) {
    return <>{children}</>
  }

  // BDSJEA-191: durante la carga inicial de permisos (post-login) el store
  // arranca con permisos=[] antes de que getProfiles() responda. Mostrar el
  // skeleton evita el flash de la pantalla de acceso denegado.
  if (pantalla && loading) {
    return <Loading loading />
  }

  if (fallback !== undefined) {
    return fallback
  }

  if (!pantalla) {
    return null
  }

  // Se arma con Card y estilos propios en vez de con Alert de reactstrap: la
  // tipografía del tema estaba dejando el título y el texto sin renderizar y el
  // usuario veía un recuadro vacío con un botón, que es peor que no mostrar
  // nada. Acá el color y el tamaño de cada línea son explícitos.
  return (
    <Container className="mt-4" style={{ maxWidth: 640 }}>
      <Card>
        <CardBody className="text-center p-4">
          <i
            className="mdi mdi-lock-outline"
            style={{ fontSize: 48, color: "#adb5bd", lineHeight: 1 }}
          />
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: "#343a40",
              marginTop: 12,
            }}
          >
            No tenés acceso a esta sección
          </div>
          <div style={{ color: "#6c757d", marginTop: 8 }}>
            Tu perfil no incluye permisos para esta pantalla.
          </div>
          <div style={{ color: "#6c757d", marginTop: 4, fontSize: 13 }}>
            {roles ? `Requiere el perfil ${roles.join(" o ")}. ` : ""}Si creés
            que se trata de un error, pedile a un administrador que revise tus
            perfiles.
          </div>
          <Button
            color="primary"
            className="mt-4"
            onClick={() => history.push("/agro/dashboard")}
          >
            Volver al inicio
          </Button>
        </CardBody>
      </Card>
    </Container>
  )
}

RequireRole.propTypes = {
  roles: PropTypes.arrayOf(PropTypes.string),
  objeto: PropTypes.string,
  accion: PropTypes.oneOf(["consulta", "modificacion"]),
  pantalla: PropTypes.bool,
  children: PropTypes.node,
  fallback: PropTypes.node,
}

RequireRole.defaultProps = {
  pantalla: false,
  accion: "consulta",
}

export default RequireRole
